import { useState, useEffect } from 'react';
import { api } from '@/lib/api';
import type { WeatherDay } from '../types';

const CACHE_KEY = 'fias_calendar_weather';
const CACHE_TTL_MS = 3 * 60 * 60 * 1000; // 3 hours

interface CachedWeather {
  data: Record<string, WeatherDay>;
  timestamp: number;
  lat: number;
  lon: number;
}

function getCachedWeather(): CachedWeather | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedWeather;
    if (Date.now() - parsed.timestamp > CACHE_TTL_MS) return null;
    return parsed;
  } catch {
    return null;
  }
}

function setCachedWeather(data: Record<string, WeatherDay>, lat: number, lon: number) {
  try {
    const entry: CachedWeather = { data, timestamp: Date.now(), lat, lon };
    localStorage.setItem(CACHE_KEY, JSON.stringify(entry));
  } catch {
    // localStorage full — ignore
  }
}

// Module-level geolocation cache to avoid re-requesting on every mount
const GEO_CACHE_TTL_MS = 30 * 60 * 1000; // 30 minutes
let cachedGeoCoords: { lat: number; lon: number; timestamp: number } | null = null;

function getCachedGeoCoords(): { lat: number; lon: number } | null {
  if (!cachedGeoCoords) return null;
  if (Date.now() - cachedGeoCoords.timestamp > GEO_CACHE_TTL_MS) {
    cachedGeoCoords = null;
    return null;
  }
  return { lat: cachedGeoCoords.lat, lon: cachedGeoCoords.lon };
}

function setCachedGeoCoords(lat: number, lon: number) {
  cachedGeoCoords = { lat, lon, timestamp: Date.now() };
}

// Timezone-based fallback coordinates (major cities)
const TIMEZONE_COORDS: Record<string, [number, number]> = {
  'America/New_York': [40.71, -74.01],
  'America/Chicago': [41.88, -87.63],
  'America/Denver': [39.74, -104.99],
  'America/Los_Angeles': [34.05, -118.24],
  'America/Toronto': [43.65, -79.38],
  'Europe/London': [51.51, -0.13],
  'Europe/Paris': [48.86, 2.35],
  'Europe/Berlin': [52.52, 13.41],
  'Asia/Tokyo': [35.68, 139.69],
  'Asia/Shanghai': [31.23, 121.47],
  'Australia/Sydney': [-33.87, 151.21],
};

function getFallbackCoords(): [number, number] {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return TIMEZONE_COORDS[tz] ?? [40.71, -74.01]; // Default to NYC
}

async function fetchWeather(lat: number, lon: number): Promise<Record<string, WeatherDay>> {
  // Route through backend proxy (Entity-tracked) instead of calling Open-Meteo directly.
  // The backend handles caching and entity metrics recording.
  const res = await api.get<{ weather: Record<string, WeatherDay> }>(
    `/arches/calendar/weather?lat=${lat}&lon=${lon}`,
  );
  return res.data.weather;
}

export function useWeather(enabled: boolean): Map<string, WeatherDay> {
  const [weather, setWeather] = useState<Map<string, WeatherDay>>(new Map());

  useEffect(() => {
    if (!enabled) {
      setWeather(new Map());
      return;
    }

    // Check cache first
    const cached = getCachedWeather();
    if (cached) {
      setWeather(new Map(Object.entries(cached.data)));
      return;
    }

    let cancelled = false;

    const load = async (lat: number, lon: number) => {
      try {
        const data = await fetchWeather(lat, lon);
        if (!cancelled) {
          setCachedWeather(data, lat, lon);
          setWeather(new Map(Object.entries(data)));
        }
      } catch {
        // Silently fail — weather is optional
      }
    };

    const cachedGeo = getCachedGeoCoords();
    if (cachedGeo) {
      load(cachedGeo.lat, cachedGeo.lon);
    } else if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          if (!cancelled) {
            setCachedGeoCoords(pos.coords.latitude, pos.coords.longitude);
            load(pos.coords.latitude, pos.coords.longitude);
          }
        },
        () => {
          // Permission denied — use fallback
          const [lat, lon] = getFallbackCoords();
          if (!cancelled) load(lat, lon);
        },
        { timeout: 5000 },
      );
    } else {
      const [lat, lon] = getFallbackCoords();
      load(lat, lon);
    }

    return () => {
      cancelled = true;
    };
  }, [enabled]);

  return weather;
}
